import Cookies from "js-cookie";
import { getActiveUser } from "@noom/hive-auth";
import { send } from "./fetch";
import { appConfig } from "src/config";

/**
 * Helper for parsing the cookie for autologin data (in app authentication).
 *
 * Copyright (C) 2020 Noom Inc.
 * Author: Sumin
 */

const IN_APP_COOKIE = "autologin_data";
const WEB_COOKIE = "identity_data";
const ACCESS_CODE = "accessCode";
const ACCESS_TOKEN = "accessToken";

export function getInAppAutoCookie() {
  return getAuthCookie(IN_APP_COOKIE);
}

export function getWebAuthCookie() {
  return getAuthCookie(WEB_COOKIE);
}

export function getAuthCookie(cookieName: string) {
  const cookie = Cookies.get(cookieName);
  if (cookie) {
    return parseAuthCookie(cookie);
  }
  return null;
}

function parseAuthCookie(cookie: string) {
  // Potential type leakage, but these should probably be opaque anyway
  const parsedCookieDict = JSON.parse(cookie) as Record<string, string>;
  if (ACCESS_TOKEN in parsedCookieDict && ACCESS_CODE in parsedCookieDict) {
    return parsedCookieDict;
  }
  return null;
}

export async function setInAppAutoCookie(
  accessCode: string,
  accessToken: string,
  email = "jupst1@ta.noom.com",
  reloadOnSuccess = true
) {
  if (__NODE_ENV__ !== "production" && (!accessCode || !accessToken)) {
    throw new Error(
      "accessCode and accessToken must be provided. See WorkingWithInAppUpsells in app handbook"
    );
  }

  const cookie = {
    accessCode,
    accessToken,
    email,
  };
  Cookies.set(IN_APP_COOKIE, JSON.stringify(cookie), {
    expires: new Date(Date.now() + 24 * 60 * 60 * 1000),
    domain:
      process.env.NODE_ENV !== "production"
        ? undefined
        : appConfig.COOKIE_DOMAIN,
  });

  // Force update of the user id cookie
  await send("POST", "/internal/autologin/");
  if (reloadOnSuccess) {
    window.location.reload();
  }
}

/**
 * Access code from in-app (`autologin_data`) or web (`identity_data`) auth
 * cookies. Prefer hive-auth when available; use this when `getActiveUser()` is
 * still empty — hive hydrates on a fresh mount, so right after checkout in the
 * same SPA session cookie/Redux already have the access code while hive does not.
 */
export function getCookieAccessCode(): string | undefined {
  return getInAppAutoCookie()?.accessCode ?? getWebAuthCookie()?.accessCode;
}

/**
 * @deprecated Prefer hive-auth: `getActiveUser()?.accessCode` /
 * `getActiveUser()?.accessToken`. When hive is still empty, use cookie
 * fallbacks (`getCookieAccessCode` / this helper) — see `getCookieAccessCode`.
 */
export function getAccessToken() {
  const inAppAuthCookie = getInAppAutoCookie();
  if (inAppAuthCookie?.[ACCESS_TOKEN]) {
    return inAppAuthCookie[ACCESS_TOKEN];
  }
  const webAuthCookie = getWebAuthCookie();
  return webAuthCookie?.[ACCESS_TOKEN];
}

/**
 * Access token from hive-auth, falling back to the legacy auth cookies.
 *
 * Both sources are required. hive-auth persists its own state under the
 * `hive-auth` cookie, so a user who authenticated through the identity server
 * has no `identity_data`/`autologin_data` cookie and `getAccessToken()` alone
 * returns undefined. Conversely, native apps inject `autologin_data` via
 * Set-Cookie and Growth sets `identity_data` on account creation, either of
 * which can be present before hive has hydrated.
 */
export function getUserAccessToken(): string | undefined {
  return getActiveUser()?.accessToken || getAccessToken();
}

/**
 * Whether there is a signed-in user, from hive-auth or the legacy auth cookies.
 *
 * Inside React prefer `useAuth().isAuthenticated`, which re-renders when auth
 * state changes. This is a point-in-time read for module-level code — `goto`
 * handlers, page-set conditions, eligibility checks — where hooks are
 * unavailable.
 *
 * Presence only — neither this nor `useAuth().isAuthenticated` checks
 * whether the token has expired. Callers that need a usable session
 * must pair it with `useAuth().shouldReLogin()`, as `AuthGuard` and
 * `PageSetAuthGuard` do. Those guards refresh an expired session before
 * redirecting to login.
 */
export function isUserAuthenticated(): boolean {
  return !!getUserAccessToken();
}
